// GroupModal.tsx
//
// Cadastro de GRUPO DE PRODUTO (Suprimentos). Grupo é o agrupamento interno de
// compra/estoque — "Laticínios", "Descartáveis" —, por loja.
//
// O QUE O DADO REAL DIZ: a DynamicTable monta esta lista filtrando
// `decryptedInfoToken.subsidiaryInfo` por `subsidiaryInfoType === 1` (case 3).
// Só que, na rede de verdade, as linhas de tipo 1 são outras duas coisas:
//   (a) os três tipos de movimento (entrada, saída, transferência), com o valor
//       gravado em TEXTO PURO, não em JSON; e
//   (b) o roteamento de categoria para impressora, uma linha por loja.
// Ou seja: NÃO EXISTE grupo de produto gravado hoje. A lista vir vazia é o
// estado correto, não uma falha de carregamento — e é por isso que a tela diz
// isso em uma frase, em vez de deixar o usuário procurando o que não existe.
//
// ONDE GRAVA: `POST/PATCH suprimentos/grupos`, a tabela `asup."Grupo"`. Quem
// monta a requisição é o `handleSave` da Configuração — por isso o payload
// daqui usa OS NOMES DE COLUNA do agregado (`subsidiary`, `nome`, `descricao`,
// `status`) e não os nomes do InfoToken: o servidor descarta em silêncio toda
// chave fora da lista branca do agregado.
//
// A LISTA continua vindo de `subsidiaryInfo` tipo 1 — que, como se lê acima,
// não contém grupo nenhum. Grupo criado aqui grava no banco e não aparece na
// tabela enquanto a lista não passar a ler o REST paginado.
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import * as AlfredoStyles from "../../../styles/components/configuration/modals/_casca";
import { useModalAcessivel } from "../../../hooks/useModalAcessivel";

interface ModalPadraoProps {
  selectedObject: any;
  handleCloseModal: () => void;
  handleSave: (objectType: string, payload?: any) => any;
  parseInfoValue: (infoValue: string) => any;
  decryptedInfoToken?: any;
}

const GroupModal: React.FC<ModalPadraoProps> = ({
  selectedObject,
  handleCloseModal,
  handleSave,
  parseInfoValue,
  decryptedInfoToken,
}) => {
  // Esc fecha, o foco fica preso dentro e volta para quem abriu, e o leitor
  // de tela anuncia a janela pelo titulo. Ver hooks/useModalAcessivel.ts.
  const { refDoModal, propsDoDialogo } = useModalAcessivel({
    aoFechar: handleCloseModal,
    idDoTitulo: "titulo-groupmodal",
  });

  // `{}` é truthy (useModal faz `setSelectedObject(object || {})`): quem diz se
  // é edição é o uuid, não a existência do objeto. `grupoUuid` vem primeiro
  // porque é a chave que o `handleSave` do shell lê para escolher entre POST e
  // PATCH — divergir dela faria o modal dizer "Editar" enquanto o shell criava
  // uma linha nova.
  const uuidEmEdicao =
    selectedObject?.grupoUuid ||
    selectedObject?.productGroupUUID ||
    selectedObject?.productGroupUuid ||
    selectedObject?.subsidiaryInfoUuid ||
    "";
  const ehEdicao = Boolean(uuidEmEdicao);
  const tipoDoModal = ehEdicao ? "updateGroup" : "createGroup";

  // Os campos têm o nome DA COLUNA do agregado `grupos`. `status` é a coluna
  // BOOLEAN de ativo/inativo da tabela — não existe coluna `ativo` lá.
  const [form, setForm] = useState({
    nome: "",
    descricao: "",
    subsidiary: "",
    status: true,
  });
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState<{
    tom: "ok" | "erro" | "atencao";
    texto: string;
  } | null>(null);

  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  /** As lojas reais: organization -> companies -> subsidiaries. */
  const lojas: any[] = useMemo(() => {
    const empresas = decryptedInfoToken?.organization?.companies ?? [];
    return empresas.flatMap((empresa: any) =>
      (empresa?.subsidiaries ?? []).map((filial: any) => ({
        ...filial,
        companyName: empresa?.companyName,
      }))
    );
  }, [decryptedInfoToken]);

  useEffect(() => {
    // ARMADILHA desta tela: em `subsidiaryInfo` tipo 1 o valor às vezes é TEXTO
    // PURO (os tipos de movimento do legado), não JSON. `parseInfoValue` sobre
    // isso devolve o que devolver — por isso o espalhamento só acontece quando
    // o resultado é objeto, e nunca substitui os campos já achatados.
    const cru = selectedObject?.subsidiaryInfoValue;
    let doJson: any = {};
    if (cru && typeof parseInfoValue === "function") {
      const lido = parseInfoValue(cru);
      if (lido && typeof lido === "object") doJson = lido;
    }
    const valores = { ...(selectedObject ?? {}), ...doJson };

    setForm({
      nome:
        valores.nome ||
        valores.productGroupName ||
        valores.subsidiaryInfoDescription ||
        "",
      descricao: valores.descricao || valores.productGroupDescription || "",
      subsidiary: valores.subsidiary || valores.subsidiaryUuid || "",
      status:
        (valores.status ?? valores.ativo ?? valores.subsidiaryInfoStatus) !==
        false,
    });
    setAviso(null);
  }, [selectedObject, parseInfoValue]);

  const handleLocalChange = (campo: string, valor: any) => {
    setForm((anterior) => ({ ...anterior, [campo]: valor }));
  };

  const validar = (): string | null => {
    if (!form.nome.trim()) return "O nome do grupo é obrigatório.";
    return null;
  };

  const handleSubmit = async () => {
    const erro = validar();
    if (erro) {
      setAviso({ tom: "erro", texto: erro });
      return;
    }

    setSalvando(true);
    try {
      const r = await handleSave(tipoDoModal, {
        ...form,
        nome: form.nome.trim(),
        descricao: form.descricao.trim(),
        grupoUuid: uuidEmEdicao || undefined,
      });
      if (r && !r.ok && montado.current) {
        setAviso({ tom: r.tom || "erro", texto: r.texto });
      }
    } finally {
      if (montado.current) setSalvando(false);
    }
  };

  return ReactDOM.createPortal(
    <AlfredoStyles.ModalOverlay>
      <AlfredoStyles.ModalContent ref={refDoModal} {...propsDoDialogo}>
        <AlfredoStyles.ModalHeader>
          <AlfredoStyles.ModalTitle id="titulo-groupmodal">
            <AlfredoStyles.MaterialIcons>
              folder_open
            </AlfredoStyles.MaterialIcons>
            {ehEdicao ? "Editar Grupo" : "Novo Grupo"}
          </AlfredoStyles.ModalTitle>
          <AlfredoStyles.ModalCloseButton onClick={handleCloseModal}>
            <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
          </AlfredoStyles.ModalCloseButton>
        </AlfredoStyles.ModalHeader>

        <AlfredoStyles.ModalBody>
          <AlfredoStyles.FormContainer>
            {/* Antes dos campos: quem abriu a lista e a viu vazia precisa saber
                que vazia é o certo, e não ficar procurando o carregamento que
                falhou. */}
            <AlfredoStyles.AlertMessage $tom="atencao">
              Não existe <strong>grupo de produto gravado hoje</strong> na rede:
              as linhas de <code>subsidiaryInfo</code> tipo 1 que a lista lê são,
              no dado real, os três tipos de movimento (com valor em texto puro)
              e o roteamento de categoria para impressora, por loja. A lista
              vazia é o estado correto, não um erro de carregamento.
            </AlfredoStyles.AlertMessage>

            <AlfredoStyles.FormSection>
              <AlfredoStyles.SectionTitle>
                Dados do grupo
              </AlfredoStyles.SectionTitle>

              <AlfredoStyles.FormGrid>
                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Nome *</AlfredoStyles.Label>
                  <AlfredoStyles.Input
                    type="text"
                    value={form.nome}
                    onChange={(e) =>
                      handleLocalChange("nome", e.target.value)
                    }
                    placeholder="Ex.: Laticínios, Descartáveis"
                  />
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer>
                  <AlfredoStyles.Label>Loja</AlfredoStyles.Label>
                  <AlfredoStyles.Select
                    value={form.subsidiary}
                    onChange={(e) =>
                      handleLocalChange("subsidiary", e.target.value)
                    }
                  >
                    <option value="">Selecione a loja</option>
                    {lojas.map((loja: any) => (
                      <option
                        key={loja.subsidiaryUuid}
                        value={loja.subsidiaryUuid}
                      >
                        {loja.subsidiaryName}
                        {loja.companyName ? ` — ${loja.companyName}` : ""}
                      </option>
                    ))}
                  </AlfredoStyles.Select>
                </AlfredoStyles.LabelInputContainer>

                <AlfredoStyles.LabelInputContainer fullWidth>
                  <AlfredoStyles.Label>Descrição</AlfredoStyles.Label>
                  <AlfredoStyles.TextArea
                    value={form.descricao}
                    onChange={(e) =>
                      handleLocalChange("descricao", e.target.value)
                    }
                    placeholder="O que entra neste grupo"
                  />
                </AlfredoStyles.LabelInputContainer>
              </AlfredoStyles.FormGrid>

              <AlfredoStyles.CheckboxContainer>
                <AlfredoStyles.CheckboxInput>
                  <input
                    type="checkbox"
                    checked={form.status}
                    onChange={(e) =>
                      handleLocalChange("status", e.target.checked)
                    }
                  />
                  <span className="checkmark"></span>
                </AlfredoStyles.CheckboxInput>
                <AlfredoStyles.CheckboxLabel>
                  Grupo ativo
                  <span className="tooltip">
                    Desmarque para tirar o grupo de uso sem apagá-lo
                  </span>
                </AlfredoStyles.CheckboxLabel>
              </AlfredoStyles.CheckboxContainer>
            </AlfredoStyles.FormSection>

            <AlfredoStyles.AlertMessage $tom="atencao">
              O que você salvar aqui grava em{" "}
              <strong>suprimentos/grupos</strong>, mas a tabela acima ainda lê a
              lista de <code>subsidiaryInfo</code> tipo 1 — que, como está dito
              acima, não contém grupo nenhum. Ou seja: o grupo é criado no banco
              e <strong>não aparece na lista</strong> até esta tela passar a ler
              o REST paginado. Não é o salvamento que falhou.
            </AlfredoStyles.AlertMessage>

            {aviso && (
              <AlfredoStyles.AlertMessage $tom={aviso.tom}>
                {aviso.texto}
              </AlfredoStyles.AlertMessage>
            )}
          </AlfredoStyles.FormContainer>
        </AlfredoStyles.ModalBody>

        <AlfredoStyles.ModalFooter>
          <AlfredoStyles.ButtonGroup>
            <AlfredoStyles.SecondaryButton onClick={handleCloseModal}>
              <AlfredoStyles.MaterialIcons>close</AlfredoStyles.MaterialIcons>
              Cancelar
            </AlfredoStyles.SecondaryButton>
            <AlfredoStyles.PrimaryButton
              onClick={handleSubmit}
              disabled={salvando}
            >
              <AlfredoStyles.MaterialIcons>save</AlfredoStyles.MaterialIcons>
              {salvando
                ? "Salvando..."
                : ehEdicao
                ? "Atualizar grupo"
                : "Criar grupo"}
            </AlfredoStyles.PrimaryButton>
          </AlfredoStyles.ButtonGroup>
        </AlfredoStyles.ModalFooter>
      </AlfredoStyles.ModalContent>
    </AlfredoStyles.ModalOverlay>,
    document.body
  );
};

export default GroupModal;
